import {
    isCatalogImportCode,
    isCatalogImportLinesInvalid,
} from '@/api/handlers/resources/catalogImports';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { CatalogImportResult } from '@/api/models';
import {
    noticeFor,
    type CatalogImportNotice,
} from '@/pages/catalogImport/notices';
import { needsAttentionCount } from '@/pages/catalogImport/store';

/**
 * What the step says about the last Import press that did not end on
 * Result. Pure data, so the View only renders it, with Retry:
 *
 * - `refused`: the import said why, one sentence each: the header's problems
 *   of a refusal (an archived branch, accounting not ready, too many units),
 *   or a refusal's own code (`BRANCH_NOT_ALLOWED`, `IMPORT_CONFLICT`).
 * - `failed`: no answer, or one the app cannot describe.
 *
 * A refusal because rows need attention is not here: the table shows those
 * rows, and a toast says how many.
 */
export type ImportSubmitState =
    | { status: 'idle' }
    | { status: 'refused'; notices: CatalogImportNotice[] }
    | { status: 'failed' };

export const IMPORT_SUBMIT_IDLE: ImportSubmitState = { status: 'idle' };

/** What a refused or unanswered import means for the step. */
export type ImportRefusal = {
    /** The fresh per-row answer the refusal carried, to show in place of the preview. */
    preview: CatalogImportResult | null;
    /** Its rows that need attention and are not excluded: the toast's count. */
    needsAttention: number;
    submit: ImportSubmitState;
};

/**
 * Reads the import's rejection:
 *
 * 1. **`IMPORT_LINES_INVALID` with its result** (nothing was written): the
 *    result replaces the preview. Header problems are said with Retry;
 *    rows needing attention are counted for the toast. When it names
 *    neither, the catalog changed between the import's own check and the
 *    answer it sent back (the server's wording), which is a conflict:
 *    importing again checks again.
 * 2. **Any other code of the import** (including `IMPORT_LINES_INVALID`
 *    without a result) is its sentence, with Retry.
 * 3. **Anything else** (no answer, a code of the books such as
 *    `ENTRY_NUMBER_COLLISION`) failed: Retry.
 */
export const importRefusalOf = (
    error: ErrorResponse | null,
    exclusions: ReadonlySet<number>,
): ImportRefusal => {
    if (isCatalogImportLinesInvalid(error)) {
        const { result } = error;
        const needsAttention = needsAttentionCount({
            preview: result,
            exclusions,
        });
        const headerNotices = result.header.problems.map((problem) =>
            noticeFor(problem),
        );
        if (headerNotices.length > 0) {
            return {
                preview: result,
                needsAttention,
                submit: { status: 'refused', notices: headerNotices },
            };
        }
        return {
            preview: result,
            needsAttention,
            submit:
                needsAttention > 0
                    ? IMPORT_SUBMIT_IDLE
                    : {
                          status: 'refused',
                          notices: [noticeFor({ code: 'IMPORT_CONFLICT' })],
                      },
        };
    }

    if (error?.code !== undefined && isCatalogImportCode(error.code)) {
        return {
            preview: null,
            needsAttention: 0,
            submit: {
                status: 'refused',
                notices: [noticeFor({ code: error.code })],
            },
        };
    }

    return { preview: null, needsAttention: 0, submit: { status: 'failed' } };
};
